import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Square, Loader2, AudioLines } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('adminToken');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const pickMimeType = () => {
  const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const type of candidates) {
    if (window.MediaRecorder?.isTypeSupported?.(type)) return type;
  }
  return '';
};

const extFor = (mime) => (mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm');

/** Record a voice note on the phone, transcribe it with Whisper, hand the text back. */
export const VoiceRecorder = ({ onTranscript, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error('Recording is not supported on this browser — type it instead');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => upload(recorder.mimeType || mimeType || 'audio/webm');
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s >= 119) { stop(); return s; }   // hard stop at 2 minutes
        return s + 1;
      }), 1000);
    } catch (err) {
      toast.error('Microphone blocked. Allow mic access and try again.');
    }
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const upload = async (mimeType) => {
    const blob = new Blob(chunksRef.current, { type: mimeType });
    if (blob.size < 1200) {
      toast.error('That was too short — hold on and speak for a couple of seconds');
      return;
    }
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('file', blob, `voice-note.${extFor(mimeType)}`);
      const { data } = await authAxios.post(`${API}/work-orders/ai/transcribe`, form);
      onTranscript?.(data.text);
      toast.success('Transcribed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not transcribe that recording');
    } finally {
      setTranscribing(false);
    }
  };

  if (transcribing) {
    return (
      <div className="flex items-center gap-2 px-4 min-h-[48px] rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300" data-testid="voice-transcribing">
        <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> Transcribing…
      </div>
    );
  }

  if (recording) {
    return (
      <button
        type="button"
        onClick={stop}
        className="flex items-center justify-center gap-2 px-4 min-h-[48px] w-full rounded-xl bg-red-500/20 border border-red-400/50 text-red-100 font-medium"
        data-testid="voice-stop-btn"
      >
        <Square className="w-4 h-4 fill-current" />
        Stop &amp; transcribe
        <span className="font-mono text-sm tabular-nums">
          {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
        </span>
        <AudioLines className="w-4 h-4 animate-pulse" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className="flex items-center justify-center gap-2 px-4 min-h-[48px] w-full rounded-xl bg-white/5 border border-white/15 text-slate-200 hover:bg-white/10 disabled:opacity-50"
      data-testid="voice-record-btn"
    >
      <Mic className="w-4 h-4 text-cyan-300" /> Speak it instead
    </button>
  );
};

export default VoiceRecorder;
